// Return-state checklist (design §6.3 / D11): the free-form, any-order path for a user who
// came back half-set-up — some progress made, but first-run completion never recorded. Unlike
// the wizard it gates nothing; every item is optional and lights up from live readiness. The
// terminal "Go to dashboard" persists first-run completion (via the gate's onComplete) so the
// next load lands on the dashboard.
//
// No per-item nav: the gate renders this BEFORE the dashboard shell mounts, so a `/sources`
// link would be dead — the real Sources/People flows live in the dashboard the user reaches
// by completing here. Copy is verbatim from §6.3.

import { useState } from 'react';
import { Checklist } from '../shared/Checklist';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { usePendingInvites } from '../../hooks/useTeamSettings';

export function ReturnChecklist({ onComplete }: { onComplete: () => void | Promise<void> }) {
  const { data } = useOnboardingReadiness();
  const { data: pending } = usePendingInvites();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const extensionConnected = data?.extensionConnected ?? false;
  const hasSource = (data?.connectedSourceCount ?? 0) > 0;
  // member_count includes the user, so >1 means at least one teammate has joined.
  const hasTeammates = (pending?.current_team?.member_count ?? 0) > 1;

  const items = [
    { id: 'extension', label: 'Install the Terum extension', done: extensionConnected },
    { id: 'sources', label: 'Connect more sources', done: hasSource },
    {
      id: 'team',
      label:
        "Invite members to your team — or, to join an existing team, ask that team's admin for an invite link and open it.",
      done: hasTeammates,
    },
  ];

  const go = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await onComplete();
      // on success the gate unmounts this screen
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className="v2-content ov-surface">
      <div className="wrap">
        <div className="shead">
          <div>
            <div className="crumb">GET STARTED</div>
            <h1 className="greeting">Almost there — <span className="em">finish setup</span>.</h1>
          </div>
        </div>
        <p className="sub" style={{ marginBottom: 18 }}>
          Do these in any order — they light up as you go.
        </p>
        <Checklist items={items} />
        <button className="btn btn-primary" style={{ marginTop: 24 }} disabled={busy} onClick={go}>
          {busy ? 'Saving…' : <>Go to dashboard <span className="arr">→</span></>}
        </button>
        {failed && (
          <p style={{ marginTop: 8, fontSize: 13, color: 'var(--rose)' }}>
            Couldn't save — please try again.
          </p>
        )}
      </div>
    </div>
  );
}
